import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { CTABanner } from "@/components/Shared";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, LandPlot } from "lucide-react";
import { IMAGES } from "@/data/content";

const UPCOMING = [
  {
    icon: Building2,
    type: "Redevelopment",
    title: "Redevelopment Opportunities",
    status: "Under Evaluation",
    description:
      "We are studying redevelopment possibilities in established urban areas. Project names, locations and details will be published here as opportunities are formalised.",
  },
  {
    icon: LandPlot,
    type: "Plotted Land Development",
    title: "Plotted Development Opportunities",
    status: "Under Evaluation",
    description:
      "We are identifying land parcels suited to thoughtfully planned plotted developments. Details will be announced here as layouts are planned and approvals progress.",
  },
];

const Developments = () => (
  <>
    <SEO
      title="Our Developments | Evaya Ventures"
      description="Evaya Ventures' development portfolio is evolving. We are currently evaluating opportunities across redevelopment and plotted land development."
      path="/developments"
      breadcrumbs={[{ name: "Home", path: "/" }, { name: "Developments", path: "/developments" }]}
    />

    <PageHero
      overline="Our Developments"
      title="Our development portfolio is evolving"
      description="We are currently evaluating and developing opportunities across redevelopment and plotted land development. Details of upcoming developments will be announced as they progress."
      image={IMAGES.concrete}
      imageAlt={IMAGES.concreteAlt}
      testId="developments-hero"
    />

    <section className="bg-white py-24 lg:py-32" data-testid="developments-upcoming">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-dark">Upcoming Opportunities</p>
          <h2 className="mt-4 font-display text-4xl leading-tight tracking-tight text-ink md:text-5xl">
            Announced here first, as they take shape
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink/65 md:text-lg">
            As an emerging development company, we believe in announcing only what is real. Each opportunity below represents a focus area where our evaluation and planning work is actively underway.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-10">
          {UPCOMING.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.1}>
              <article
                data-testid={`upcoming-card-${i + 1}`}
                className="flex h-full flex-col border border-dashed border-ink/25 bg-sand p-8 lg:p-12"
              >
                <div className="flex items-center justify-between gap-4">
                  <item.icon className="h-9 w-9 text-brand-dark" strokeWidth={1.5} aria-hidden="true" />
                  <span className="border border-brand/50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-dark" data-testid={`upcoming-status-${i + 1}`}>
                    {item.status}
                  </span>
                </div>
                <p className="mt-7 text-xs font-semibold uppercase tracking-[0.24em] text-ink/50">{item.type}</p>
                <h3 className="mt-2 font-display text-3xl text-ink">{item.title}</h3>
                <p className="mt-4 flex-1 text-base leading-relaxed text-ink/65">{item.description}</p>
                <div className="mt-8">
                  <Link
                    to="/contact"
                    data-testid={`upcoming-interest-button-${i + 1}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide text-ink transition-colors duration-300 hover:text-brand"
                  >
                    Register Your Interest
                    <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-ink/10 bg-sand py-24 lg:py-32" data-testid="developments-commitment">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-dark">Our Commitment</p>
            <h2 className="mt-4 font-display text-4xl leading-tight tracking-tight text-ink md:text-5xl">
              Transparency before promotion
            </h2>
            <p className="mt-5 text-base leading-relaxed text-ink/65 md:text-lg">
              We will never present concepts as completed projects or inflate our track record. When a development is ready to be shared, it will appear here with accurate details — name, location, development type, status, description, imagery and an enquiry channel.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-6">
          <div className="relative">
            <div className="absolute -right-4 -top-4 h-full w-full border border-brand/40" aria-hidden="true" />
            <img src={IMAGES.facade} alt={IMAGES.facadeAlt} loading="lazy" className="relative h-[380px] w-full object-cover" />
          </div>
        </Reveal>
      </div>
    </section>

    <CTABanner title="Want to hear about developments first?" copy="Leave us your details and the kind of opportunity you are interested in. We will reach out as developments are announced." />
  </>
);

export default Developments;
